"use client";

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from '@/components/ui/input';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const RevenueLossCalculator: React.FC<Props> = ({ isOpen, onClose }) => {
    // State for the new LTV calculator
    const [missedCalls, setMissedCalls] = useState('25');
    const [avgServicePrice, setAvgServicePrice] = useState('30');
    const [visitFrequency, setVisitFrequency] = useState('12');
    const [customerRetention, setCustomerRetention] = useState('3');
    const [ltvLoss, setLtvLoss] = useState(0);

    // State for the Order Take calculator
    const [orderMissedCalls, setOrderMissedCalls] = useState('25');
    const [orderAvgServicePrice, setOrderAvgServicePrice] = useState('25');
    const [orderLostRevenue, setOrderLostRevenue] = useState(0);

    // State for the Ads Budget calculator
    const [adsMissedCalls, setAdsMissedCalls] = useState('25');
    const [cpl, setCpl] = useState('15');
    const [burnedBudget, setBurnedBudget] = useState(0);

    // State for the Time Wasted calculator
    const [faqCalls, setFaqCalls] = useState('100');
    const [avgCallDuration, setAvgCallDuration] = useState('3');
    const [hourlyRate, setHourlyRate] = useState('15');
    const [timeWastedCost, setTimeWastedCost] = useState(0);

    useEffect(() => {
        // New LTV calculator logic
        const numMissedCalls = parseFloat(missedCalls);
        const numAvgServicePrice = parseFloat(avgServicePrice);
        const numVisitFrequency = parseFloat(visitFrequency);
        const numCustomerRetention = parseFloat(customerRetention);

        if (numMissedCalls > 0 && numAvgServicePrice > 0 && numVisitFrequency > 0 && numCustomerRetention > 0) {
            const calculatedLtvLoss = numMissedCalls * numAvgServicePrice * numVisitFrequency * numCustomerRetention;
            setLtvLoss(calculatedLtvLoss);
        } else {
            setLtvLoss(0);
        }

        // Order Take calculator logic
        const numOrderMissedCalls = parseFloat(orderMissedCalls);
        const numOrderAvgServicePrice = parseFloat(orderAvgServicePrice);

        if (numOrderMissedCalls > 0 && numOrderAvgServicePrice > 0) {
            const calculatedOrderLostRevenue = numOrderMissedCalls * numOrderAvgServicePrice;
            setOrderLostRevenue(calculatedOrderLostRevenue);
        } else {
            setOrderLostRevenue(0);
        }

        // Ads Budget calculator logic
        const numAdsMissedCalls = parseFloat(adsMissedCalls);
        const numCpl = parseFloat(cpl);

        if (numAdsMissedCalls > 0 && numCpl > 0) {
            const calculatedBurnedBudget = numAdsMissedCalls * numCpl;
            setBurnedBudget(calculatedBurnedBudget);
        } else {
            setBurnedBudget(0);
        }

        // Time Wasted calculator logic
        const numFaqCalls = parseFloat(faqCalls);
        const numAvgCallDuration = parseFloat(avgCallDuration);
        const numHourlyRate = parseFloat(hourlyRate);

        if (numFaqCalls > 0 && numAvgCallDuration > 0 && numHourlyRate > 0) {
            const calculatedTimeWastedCost = (numFaqCalls * numAvgCallDuration) * (numHourlyRate / 60);
            setTimeWastedCost(calculatedTimeWastedCost);
        } else {
            setTimeWastedCost(0);
        }
    }, [missedCalls, avgServicePrice, visitFrequency, customerRetention, orderMissedCalls, orderAvgServicePrice, adsMissedCalls, cpl, faqCalls, avgCallDuration, hourlyRate]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] flex flex-col max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle className="text-2xl">Lost Revenue Calculator</DialogTitle>
                    <DialogDescription>
                        Enter your metrics and find out what you are losing.
                    </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="ltv" className="flex-1 flex flex-col min-h-0">
                    <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
                        <TabsTrigger value="ltv">LTV</TabsTrigger>
                        <TabsTrigger value="order">Order take</TabsTrigger>
                        <TabsTrigger value="adsBudget" className="hidden sm:block">Ads Budget</TabsTrigger>
                        <TabsTrigger value="timeWasted" className="hidden sm:block">Time Wasted</TabsTrigger>
                    </TabsList>
                    <TabsList className="grid w-full grid-cols-2 sm:hidden">
                        <TabsTrigger value="adsBudget">Ads Budget</TabsTrigger>
                        <TabsTrigger value="timeWasted">Time Wasted</TabsTrigger>
                    </TabsList>
                    <TabsContent value="ltv" className="overflow-y-auto pr-2">
                        <div className="py-4">
                            <div className="text-sm text-gray-600 mb-4">
                                <p><b>Application:</b> Beauty, wellness sector, subscriptions.</p>
                                <p><b>Essence:</b> A missed call from a potential new client means losing not just one appointment, but all their future visits and revenue.</p>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="missedCalls" className="block text-sm font-medium mb-2">
                                        Missed calls from new clients (per month)
                                    </label>
                                    <Input
                                        type="number"
                                        id="missedCalls"
                                        value={missedCalls}
                                        onChange={(e) => setMissedCalls(e.target.value)}
                                        placeholder="e.g., 25"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="avgServicePrice" className="block text-sm font-medium mb-2">
                                        Average service price (€)
                                    </label>
                                    <Input
                                        type="number"
                                        id="avgServicePrice"
                                        value={avgServicePrice}
                                        onChange={(e) => setAvgServicePrice(e.target.value)}
                                        placeholder="e.g., 30"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="visitFrequency" className="block text-sm font-medium mb-2">
                                        Frequency of visits per year
                                    </label>
                                    <Input
                                        type="number"
                                        id="visitFrequency"
                                        value={visitFrequency}
                                        onChange={(e) => setVisitFrequency(e.target.value)}
                                        placeholder="e.g., 12"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="customerRetention" className="block text-sm font-medium mb-2">
                                        Average customer retention (years)
                                    </label>
                                    <Input
                                        type="number"
                                        id="customerRetention"
                                        value={customerRetention}
                                        onChange={(e) => setCustomerRetention(e.target.value)}
                                        placeholder="e.g., 3"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-red-50 rounded-lg text-center border border-red-200">
                            <p className="text-base text-red-800">Potential LTV Loss:</p>
                            <p className="text-4xl font-extrabold text-red-700 mt-2">
                                {`${ltvLoss.toLocaleString('en-US', { maximumFractionDigits: 0 })}€`}
                            </p>
                        </div>

                        <div className="mt-4 pt-4 border-t">
                            <p className="text-xs text-muted-foreground">
                                <b>Formula:</b> (Missed Calls × Avg. Price) × Visit Frequency × Retention = LTV Loss
                            </p>
                             <p className="text-xs text-muted-foreground mt-2">
                                <b>Example (Beauty Salon):</b> If you miss 10 calls from new clients (monthly haircut at 30€, 3-year retention), the potential LTV loss is 18,000€ (10 calls × 30€/visit × 12 visits/year × 3 years).
                            </p>
                        </div>
                    </TabsContent>
                    <TabsContent value="order" className="overflow-y-auto pr-2">
                        <div className="py-4">
                            <div className="text-sm text-gray-600 mb-4">
                                <p><b>Application:</b> Restaurants, food delivery, any business that takes orders over the phone.</p>
                                <p><b>Essence:</b> A missed call often means a lost order. If a customer can't reach you, they'll likely order from a competitor.</p>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="orderMissedCalls" className="block text-sm font-medium mb-2">
                                        Missed calls (per month)
                                    </label>
                                    <Input
                                        type="number"
                                        id="orderMissedCalls"
                                        value={orderMissedCalls}
                                        onChange={(e) => setOrderMissedCalls(e.target.value)}
                                        placeholder="e.g., 25"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="orderAvgServicePrice" className="block text-sm font-medium mb-2">
                                        Average order value (€)
                                    </label>
                                    <Input
                                        type="number"
                                        id="orderAvgServicePrice"
                                        value={orderAvgServicePrice}
                                        onChange={(e) => setOrderAvgServicePrice(e.target.value)}
                                        placeholder="e.g., 25"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="p-6 bg-red-50 rounded-lg text-center border border-red-200">
                            <p className="text-base text-red-800">Potential Lost Revenue per month:</p>
                            <p className="text-4xl font-extrabold text-red-700 mt-2">
                                {`${orderLostRevenue.toLocaleString('en-US', { maximumFractionDigits: 0 })}€`}
                            </p>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                            <p className="text-xs text-muted-foreground">
                                <b>Formula:</b> Missed Calls × Average Order Value = Lost Revenue
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                                <b>Example (Pizzeria):</b> If you miss 10 calls and the average order is 25€, the lost revenue is 250€ (10 calls × 25€).
                            </p>
                        </div>
                    </TabsContent>
                    <TabsContent value="adsBudget" className="overflow-y-auto pr-2">
                        <div className="py-4">
                            <div className="text-sm text-gray-600 mb-4">
                                <p><b>Application:</b> Applicable to any business using pay-per-click (PPC) ads.</p>
                                <p><b>Essence:</b> When you pay for an ad that leads to a call, a missed call means you've paid for a lead you didn't capture. The ad spend is wasted.</p>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="adsMissedCalls" className="block text-sm font-medium mb-2">
                                        Missed calls from ads (per month)
                                    </label>
                                    <Input
                                        type="number"
                                        id="adsMissedCalls"
                                        value={adsMissedCalls}
                                        onChange={(e) => setAdsMissedCalls(e.target.value)}
                                        placeholder="e.g., 25"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="cpl" className="block text-sm font-medium mb-2">
                                        CPL (Cost Per Lead) (€)
                                    </label>
                                    <Input
                                        type="number"
                                        id="cpl"
                                        value={cpl}
                                        onChange={(e) => setCpl(e.target.value)}
                                        placeholder="e.g., 15"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="p-6 bg-red-50 rounded-lg text-center border border-red-200">
                            <p className="text-base text-red-800">Burned Ads Budget per month:</p>
                            <p className="text-4xl font-extrabold text-red-700 mt-2">
                                {`${burnedBudget.toLocaleString('en-US', { maximumFractionDigits: 0 })}€`}
                            </p>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                            <p className="text-xs text-muted-foreground">
                                <b>Formula:</b> Missed Calls × CPL = Burned Budget
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                                <b>Example (Car Service):</b> If you miss 10 calls from ads and the CPL is 15€, the burned budget is 150€ (10 calls × 15€).
                            </p>
                        </div>
                    </TabsContent>
                    <TabsContent value="timeWasted" className="overflow-y-auto pr-2">
                        <div className="py-4">
                            <div className="text-sm text-gray-600 mb-4">
                                <p><b>Application:</b> E-commerce, logistics, and service businesses.</p>
                                <p><b>Essence:</b> This calculator shows the potential cost of time wasted on repetitive, routine questions that a Voice AI Agent could handle, freeing up your team for higher-value work.</p>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label htmlFor="faqCalls" className="block text-sm font-medium mb-2">
                                        Routine calls handled by an employee (per month)
                                    </label>
                                    <Input
                                        type="number"
                                        id="faqCalls"
                                        value={faqCalls}
                                        onChange={(e) => setFaqCalls(e.target.value)}
                                        placeholder="e.g., 100"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="avgCallDuration" className="block text-sm font-medium mb-2">
                                        Average call duration (minutes)
                                    </label>
                                    <Input
                                        type="number"
                                        id="avgCallDuration"
                                        value={avgCallDuration}
                                        onChange={(e) => setAvgCallDuration(e.target.value)}
                                        placeholder="e.g., 3"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="hourlyRate" className="block text-sm font-medium mb-2">
                                        Employee hourly rate (€)
                                    </label>
                                    <Input
                                        type="number"
                                        id="hourlyRate"
                                        value={hourlyRate}
                                        onChange={(e) => setHourlyRate(e.target.value)}
                                        placeholder="e.g., 15"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="p-6 bg-red-50 rounded-lg text-center border border-red-200">
                            <p className="text-base text-red-800">Potential Time Wasted per month:</p>
                            <p className="text-4xl font-extrabold text-red-700 mt-2">
                                {`${timeWastedCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}€`}
                            </p>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                            <p className="text-xs text-muted-foreground">
                                <b>Formula:</b> (Calls Handled × Avg. Duration) × (Hourly Rate / 60) = Money Wasted
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                                <b>Example (E-commerce):</b> If an employee handles 10 calls (avg. 3 min) and their rate is 15€/hour, you are losing 7.50€ ((10 calls × 3 min) × (15€ / 60 min)).
                            </p>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

export default RevenueLossCalculator;
